import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Card, List, Alert, Spin, Collapse } from 'antd';
import moment from 'moment';
import { useAIAnalysis } from '../shared/hooks';
import AdditionalQuestionnaires from './AdditionalQuestionnaires';
import { QuestionnaireResponse, QuestionnaireStructure, QuestionText } from '../shared/types';

const { Panel } = Collapse;

const NewAIQuestionnaireClient = ({ client }: { client: any }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [clientAge, setClientAge] = useState<number | null>(null);
  const [existingResponses, setExistingResponses] = useState<QuestionnaireResponse[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [showExistingResponses, setShowExistingResponses] = useState(false);
  const [questionnaireStructures, setQuestionnaireStructures] = useState<Map<string, QuestionnaireStructure>>(new Map());
  const [loadingStructure, setLoadingStructure] = useState(false);

  // Use AI Analysis hook
  const { additionalQuestionnaires, loading: loadingAI, error: aiError } = useAIAnalysis(client);

  console.log('Client data in NewAIQuestionnaireClient:', client);
  const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
  const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

  // Function to fetch questionnaire structures for multiple IDs
  const fetchQuestionnaireStructures = useCallback(async (questionnaireIds: string[]) => {
    const uniqueIds = Array.from(new Set(questionnaireIds)); // Get unique IDs
    if (uniqueIds.length === 0) return;

    console.log('[NewAIQuestionnaireClient] Fetching questionnaire structures for:', uniqueIds);
    setLoadingStructure(true);

    try {
      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const structurePromises = uniqueIds.map(async (id) => {
        const response = await fetch(`${API_URL}/api/v1/questionnaires/${id}`, {
          method: "GET",
          mode: "cors",
          headers: {
            'Authorization': API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status} for ID ${id}`);
          return null;
        }

        const data: QuestionnaireStructure = await response.json();
        return { id, data };
      });

      const results = await Promise.all(structurePromises);

      setQuestionnaireStructures(prevStructures => {
        const newStructures = new Map(prevStructures);
        results.forEach(result => {
          if (result) {
            newStructures.set(result.id, result.data);
          }
        });
        console.log('[NewAIQuestionnaireClient] Questionnaire structures fetched:', newStructures);
        return newStructures;
      });

    } catch (err: any) {
      console.error('[NewAIQuestionnaireClient] Failed to fetch questionnaire structures:', err);
    } finally {
      setLoadingStructure(false);
    }
  }, [API_URL, API_KEY]);

  // Function to fetch existing questionnaire responses
  const fetchExistingResponses = useCallback(async () => {
    if (!client?.id) {
      console.error('[NewAIQuestionnaireClient] Client ID is missing:', client);
      return;
    }

    console.log('[NewAIQuestionnaireClient] Checking for existing responses for client:', client.id);
    setLoadingResponses(true);
    setResponseError(null);

    try {
      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/questionnaire-responses/user/${client.id}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[NewAIQuestionnaireClient] Existing responses fetched:', data);
      console.log('[NewAIQuestionnaireClient] Is data an array?', Array.isArray(data));
      
      // Ensure data is always an array
      const responsesArray = Array.isArray(data) ? data : (data ? [data] : []);
      setExistingResponses(responsesArray);
      
      // If there are existing responses, fetch questionnaire structures for all unique IDs
      if (responsesArray && responsesArray.length > 0) {
        const questionnaireIds = responsesArray.map(res => res.questionnaireId).filter(Boolean);
        await fetchQuestionnaireStructures(questionnaireIds);
        setShowExistingResponses(true);
      }
    } catch (err: any) {
      console.error('[NewAIQuestionnaireClient] Failed to fetch existing responses:', err);
      const errorMessage = err.message || 'Failed to fetch existing responses';
      setResponseError(errorMessage);
    } finally {
      setLoadingResponses(false);
    }
  }, [client, fetchQuestionnaireStructures, API_URL, API_KEY]);

  useEffect(() => {
    if (client?.bio?.dateOfBirth) {
      const age = moment().diff(client.bio.dateOfBirth, 'years');
      setClientAge(age);
      const baseUrl = process.env.REACT_APP_QUESTIONNAIRE_UI;
      const sessionId = client.id;
      const url = age < 15
        ? `${baseUrl}/questionnaire/myhealth-intake-child?clientId=${client.id}&email=${encodeURIComponent(client.email)}&sessionId=${encodeURIComponent(sessionId)}`
        : `${baseUrl}/questionnaire/myhealth-intake?clientId=${client.id}&email=${encodeURIComponent(client.email)}&sessionId=${encodeURIComponent(sessionId)}`;
      setIframeUrl(url);
      console.log('Determined iframe URL:', url);
      
      // Check for existing responses when component loads
      fetchExistingResponses();
    }
  }, [client, fetchExistingResponses]);

  const showModal = (questionnaireId?: string, responseId?: string) => {
    // If a specific questionnaire ID is provided, update the iframe URL
    if (questionnaireId && responseId) {
      const baseUrl = process.env.REACT_APP_QUESTIONNAIRE_UI;
      const sessionId = responseId;
      const url = `${baseUrl}/questionnaire/${questionnaireId}?clientId=${client.id}&email=${encodeURIComponent(client.email)}&sessionId=${encodeURIComponent(sessionId)}&responseId=${encodeURIComponent(responseId)}`;
      setIframeUrl(url);
      console.log('Opening specific questionnaire:', url);
    }
    setIsModalVisible(true);
  };

  const handleClose = () => {
    setIsModalVisible(false);
    // Reset to default iframe URL
    if (client?.bio?.dateOfBirth) {
      const age = moment().diff(client.bio.dateOfBirth, 'years');
      const baseUrl = process.env.REACT_APP_QUESTIONNAIRE_UI;
      const sessionId = client.id;
      const url = age < 15
        ? `${baseUrl}/questionnaire/myhealth-intake-child?clientId=${client.id}&email=${encodeURIComponent(client.email)}&sessionId=${encodeURIComponent(sessionId)}`
        : `${baseUrl}/questionnaire/myhealth-intake?clientId=${client.id}&email=${encodeURIComponent(client.email)}&sessionId=${encodeURIComponent(sessionId)}`;
      setIframeUrl(url);
    }
  };

  const toggleExistingResponses = () => {
    setShowExistingResponses(!showExistingResponses);
  };

  // Helper functions for formatting
  const formatQuestionId = (questionId: string): string => {
    // Convert question IDs like 'gen-q3' to more readable format
    return questionId.replace(/-/g, ' ').toUpperCase();
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Helper function to get question text from questionnaire structure
  const getQuestionText = (questionId: string, structure: QuestionnaireStructure | undefined, language: string = 'en'): string => {
    if (!structure) {
      return formatQuestionId(questionId); // Fallback to formatted ID
    }

    // Search through all sections and questions to find the matching question ID
    for (const section of structure.sections) {
      for (const question of section.questions) {
        if (question.id === questionId) {
          return question.text[language as keyof QuestionText] || question.text.en;
        }
      }
    }

    // If not found, return formatted question ID as fallback
    return formatQuestionId(questionId);
  };

  const renderQuestionnaireResponse = (response: QuestionnaireResponse) => {
    // Get the structure for this specific response
    const questionnaireStructure = questionnaireStructures.get(response.questionnaireId);
    
    // Create a map of questionId to answerText for easy lookup
    const answerMap = new Map(response.questionResponses.map(qr => [qr.questionId, qr.answerText]));

    return (
      <Panel
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>{questionnaireStructure?.title[response.languageCode as keyof QuestionText] || questionnaireStructure?.title.en || response.questionnaireId}</strong>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Status: {response.status} | Started: {formatDate(response.startedAt)} | Language: {response.languageCode} | Questions Answered: {response.questionResponses?.length || 0}
              </div>
            </div>
            <Button 
              type="link" 
              onClick={(e) => {
                e.stopPropagation(); // Prevent panel collapse/expand
                showModal(response.questionnaireId, response.id.toString());
              }}
              style={{ padding: 0, height: 'auto' }}
            >
              {response.status === 'IN_PROGRESS' ? 'Continue' : 'View/Edit'}
            </Button>
          </div>
        }
        key={response.id}
      >
        {questionnaireStructure ? (
          <div>
            {questionnaireStructure.sections.map(section => (
              <div key={section.id} style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: '16px', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
                  {section.title[response.languageCode as keyof QuestionText] || section.title.en}
                </h3>
                <List
                  dataSource={section.questions.sort((a, b) => a.sortOrder - b.sortOrder)}
                  renderItem={(question) => {
                    const answer = answerMap.get(question.id);
                    return (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <strong style={{ display: 'block', marginBottom: '4px' }}>
                            {question.text[response.languageCode as keyof QuestionText] || question.text.en}
                          </strong>
                          {answer !== undefined ? (
                            <div style={{ color: '#333', backgroundColor: '#f5f5f5', padding: '8px 12px', borderRadius: '4px' }}>
                              {answer}
                            </div>
                          ) : (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                              No answer provided.
                            </div>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          // Fallback to original implementation if structure is not yet loaded
          response.questionResponses && response.questionResponses.length > 0 ? (
            <List
              dataSource={response.questionResponses}
              renderItem={(questionResponse) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <strong>{getQuestionText(questionResponse.questionId, questionnaireStructure, response.languageCode)}:</strong>
                    <div style={{ marginTop: '4px', color: '#333', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                      {questionResponse.answerText}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div style={{ color: '#666', fontStyle: 'italic' }}>
              No questions answered yet.
            </div>
          )
        )}
        {response.status === 'IN_PROGRESS' && (
          <Alert
            message="This questionnaire is in progress and can be continued."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Panel>
    );
  };

  // Check if there are any IN_PROGRESS responses that can be continued
  const hasInProgressResponse = existingResponses.some(response => response.status === 'IN_PROGRESS');
  
  // Check if there are any completed responses
  const hasCompletedResponse = existingResponses.some(response => response.status === 'COMPLETED');

  return (
    <div>
      {/* Loading indicator for checking existing responses */}
      {(loadingResponses || loadingStructure) && (
        <div style={{ marginBottom: 16 }}>
          <Spin size="small" /> 
          <span>
            {loadingResponses && loadingStructure 
              ? 'Loading responses and questionnaire details...'
              : loadingResponses 
                ? 'Checking for existing responses...' 
                : 'Loading questionnaire details...'
            }
          </span>
        </div>
      )}

      {/* Error alert for response fetching */}
      {responseError && (
        <Alert
          message="Error checking existing responses"
          description={responseError}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Show existing responses if available */}
      {existingResponses.length > 0 && (
        <Card 
          title="Existing Questionnaire Responses" 
          style={{ marginBottom: 16 }}
          extra={
            <Button 
              type="text" 
              onClick={toggleExistingResponses}
              size="small"
            >
              {showExistingResponses ? 'Hide' : 'Show'} ({existingResponses.length})
            </Button>
          }
        >
          {showExistingResponses && (
            <>
              <Collapse accordion>
                {existingResponses.map((response) => renderQuestionnaireResponse(response))}
              </Collapse>
              {hasInProgressResponse && (
                <Alert
                  message="Continue Previous Questionnaire"
                  description="You have an incomplete questionnaire. Click 'Continue' to resume where you left off."
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
              {hasCompletedResponse && !hasInProgressResponse && (
                <Alert
                  message="Previous Responses Found"
                  description="You have completed this questionnaire before. You can start a new one if needed."
                  type="success"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </>
          )}
        </Card>
      )}

      {/* Main questionnaire link */}
      <Button type="link" onClick={() => showModal()} style={{ padding: 0, height: 'auto' }}>
        {hasInProgressResponse 
          ? 'Continue Questionnaire' 
          : existingResponses.length > 0 
            ? 'Start New Questionnaire'
            : clientAge !== null && clientAge > 15 
              ? 'My Health Intake - Adult Version' 
              : 'My Health Intake - Child Version'
        }
      </Button>

      <Modal
        title="My Health Intake Questionnaire"
        open={isModalVisible}
        onCancel={handleClose}
        footer={null}
        width="100%"
        style={{ top: 0, padding: 0, height: '100vh' }}
        bodyStyle={{ padding: 0, height: '100%' }}
        destroyOnClose
      >
        {iframeUrl ? (
          <iframe
            src={iframeUrl}
            title="My Health Intake Questionnaire"
            width="100%"
            height="700px"
            style={{ border: 'none' }}
          />
        ) : (
          <p>Loading questionnaire...</p>
        )}
      </Modal>

      {/* AI Recommended Additional Questionnaires */}
      <AdditionalQuestionnaires
        questionnaires={additionalQuestionnaires}
        loading={loadingAI}
        error={aiError}
        client={client}
      />
    </div>
  );
};

export default NewAIQuestionnaireClient;
